import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ExactLocation from './ExactLocation';
import IncidentTypeService from '../../services/IncidentTypeService';
import AnnouncementService from '../../services/AnnouncementService';
import TagInput from '../ui/TagInput';
import Button from '../ui/Button';

const EMERGENCY_SERVICES = [
    'Police',
    'Civil Defense / Fire',
    'Ambulance / EMS',
    'HAZMAT Team',
    'Coast Guard',
    'Search & Rescue',
    'Bomb Disposal / EOD',
    'Public Works',
    'Utilities',
];

// Mirrors DECON_TYPES in the server's AnnouncementController.
const DECON_TYPES = [
    { value: 'Fixed', hint: "Hospital's permanent decontamination facility" },
    { value: 'Mobile', hint: 'Deployed mobile decontamination unit' },
];

const cbrneHazards = [
    // Chemical Hazards
    "Ammonia",
    "Chlorine",
    "Sarin",
    "Mustard gas",
    "Phosgene",
    "VX (nerve agent)",
    "Hydrogen cyanide",
    "Carbon monoxide",
    "Sulfur dioxide",
    "Nitrogen dioxide",
    "Benzene",
    "Formaldehyde",
    "Toluene",
    "Ethylene oxide",
    "Pesticides",
    "Mercury",
    "Lead",
    "Arsenic",
    "Cyanogen chloride",
    "Hydrogen sulfide",

    // Biological Hazards
    "Anthrax",
    "Smallpox",
    "Plague",
    "Botulism",
    "Tularemia",
    "Viral hemorrhagic fevers (e.g., Ebola)",
    "Ricin",
    "Brucellosis",
    "Q fever",
    "Hantavirus",
    "Dengue fever",
    "Zika virus",
    "Cholera",
    "Tuberculosis",
    "Influenza (pandemic strains)",
    "SARS",
    "MERS",
    "HIV/AIDS",
    "Malaria",
    "West Nile virus",

    // Radiological Hazards
    "Cesium-137",
    "Cobalt-60",
    "Iodine-131",
    "Strontium-90",
    "Plutonium-239",
    "Uranium-235",
    "Radium-226",
    "Americium-241",
    "Polonium-210",
    "Radon",
    "Technetium-99m",
    "Tritium",
    "Thorium-232",
    "Curium-244",
    "Neptunium-237",
    "Promethium-147",
    "Yttrium-90",
    "Iridium-192",
    "Carbon-14",
    "Phosphorus-32",

    // Nuclear Hazards
    "Nuclear reactor meltdown",
    "Improvised nuclear device (IND)",
    "Dirty bomb (radiological dispersal device)",
    "Nuclear weapon detonation",
    "Nuclear fallout",
    "Criticality accident",
    "Plutonium exposure",
    "Neutron radiation",
    "Alpha radiation exposure",
    "Beta radiation exposure",
    "Gamma radiation exposure",
    "Spent nuclear fuel",
    "Enriched uranium",
    "Depleted uranium",
    "Reactor coolant leak",
    "Control rod failure",
    "Nuclear waste transportation accident",
    "Reprocessing plant accident",
    "Weapons-grade fissile material",
    "Fusion energy accident",

    // Explosive Hazards
    "TNT (trinitrotoluene)",
    "C-4 (composition C-4)",
    "RDX (Research Department Explosive)",
    "PETN (Pentaerythritol tetranitrate)",
    "ANFO (Ammonium nitrate/fuel oil)",
    "Dynamite",
    "Nitroglycerin",
    "Black powder",
    "TATP (Triacetone triperoxide)",
    "HMX (High Melting Explosive)",
    "Semtex",
    "Plastic explosives",
    "Landmines",
    "Improvised explosive devices (IEDs)",
    "Pipe bombs",
    "Car bombs",
    "Suicide vests",
    "Grenades",
    "Rocket-propelled grenades (RPGs)",
    "Mortar shells"
];


const MethaneForm = ({ simulate = false }) => {

    const navigate = useNavigate();

    // Incident Types
    const [incidentTypes, setIncidentTypes] = useState([]);
    const loadIncidentTypes = async () => {
        try {
            let response = await IncidentTypeService.findAll(1, 10);
            if (response && response.status === 200) {
                setIncidentTypes(response.data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        loadIncidentTypes();
    }, []);

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        isMajor: false,
        // No default — whoever raises the incident has to consciously pick one.
        deconType: "",
        location: "",
        landmark: "",
        incidentTypeIds: [],
        hazards: [],
        access: [],
        totalCasualities: '',
        greenCasualities: '',
        yellowCasualities: '',
        redCasualities: '',
        blackCasualities: '',
        erServicePresent: [],
        erServiceRequired: [],
        drill: simulate,

    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const setField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const validate = () => {
        const next = {};
        if (!formData.title.trim()) next.title = "Title is required.";
        if (!formData.description.trim()) next.description = "Description is required.";
        if (!formData.deconType) next.deconType = "Select the decon type.";
        if (formData.incidentTypeIds.length === 0) next.incidentTypeIds = "Select at least one type of hazard.";
        if (formData.totalCasualities === '') next.totalCasualities = "Total casualties is required.";
        else if (Number(formData.totalCasualities) < 0) next.totalCasualities = "Total casualties can't be negative.";
        return next;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        const hazards = formData.hazards.join(",");
        const access = formData.access.join(",");
        const incidentTypeIds = formData.incidentTypeIds.join(",");
        const totalCasualities = Number(formData.totalCasualities) || 0;
        const greenCasualities = Number(formData.greenCasualities) || 0;
        const yellowCasualities = Number(formData.yellowCasualities) || 0;
        const redCasualities = Number(formData.redCasualities) || 0;
        const blackCasualities = Number(formData.blackCasualities) || 0;
        const erServicePresent = formData.erServicePresent.join(",");
        const erServiceRequired = formData.erServiceRequired.join(",");

        const body = {
            ...formData,
            hazards, access, incidentTypeIds,
            totalCasualities, greenCasualities, yellowCasualities, redCasualities, blackCasualities,
            erServicePresent, erServiceRequired,
        }

        setSubmitting(true);
        try {
            const response = await AnnouncementService.create(body);
            if (response.data.error) {
                toast.error(response.data.message || 'Failed to create incident.');
                return;
            }
            const created = response.data.data;
            // Created as Pending — continue to the event page to assign teams and activate
            if (created?.id) navigate(`/active-incident/${created.id}`);
            else navigate(simulate ? "/activation/drills" : "/activation");
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to create incident. Please retry.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleMajor = (event) => {
        setFormData({ ...formData, isMajor: event.target.checked });
    };

    return (
        <div className="max-w-5xl">
            <h1 className="text-xl font-bold text-neutral-900 mb-1 dark:text-neutral-50">{simulate ? "Drill Activation" : "Activation"}</h1>
            <p className="text-sm text-neutral-500 mb-6 dark:text-neutral-500">
                {simulate
                    ? "Create a drill using the METHANE framework."
                    : "Create a real incident announcement using the METHANE framework."}
            </p>
            <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-neutral-200 shadow-subtle p-6 space-y-6 dark:bg-ink-900 dark:border-neutral-700">
                <label className="flex items-center gap-3 cursor-pointer w-fit">
                    <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={formData.isMajor}
                        onChange={handleMajor}
                    />
                    <span className="w-11 h-6 bg-neutral-200 peer-checked:bg-seha-500 rounded-full transition-colors relative inline-block shrink-0 dark:bg-neutral-700">
                        <span className="absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow transition-transform peer-checked:translate-x-5" />
                    </span>
                    <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">M - Major Incident</span>
                </label>

                <div>
                    <label className="form-label">Decon Type</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl" role="radiogroup" aria-label="Decon Type">
                        {DECON_TYPES.map((t) => {
                            const active = formData.deconType === t.value;
                            return (
                                <button
                                    key={t.value}
                                    type="button"
                                    role="radio"
                                    aria-checked={active}
                                    onClick={() => setField('deconType', t.value)}
                                    className={`text-left rounded-lg border px-4 py-3 transition-colors ${active
                                        ? 'border-seha-500 bg-seha-50 dark:bg-seha-900/30'
                                        : `bg-white dark:bg-ink-900 ${errors.deconType ? 'border-red-400' : 'border-neutral-200 dark:border-neutral-700'} hover:border-neutral-300`}`}
                                >
                                    <span className={`block text-sm font-semibold ${active ? 'text-seha-700 dark:text-seha-200' : 'text-neutral-800 dark:text-neutral-200'}`}>{t.value}</span>
                                    <span className="block text-xs text-neutral-500 mt-0.5">{t.hint}</span>
                                </button>
                            );
                        })}
                    </div>
                    {errors.deconType && <p className="text-xs text-red-600 mt-1">{errors.deconType}</p>}
                </div>

                <div>
                    <label className="form-label">Title</label>
                    <input
                        className={`form-input ${errors.title ? 'border-red-400 focus:ring-red-400 focus:border-red-400' : ''}`}
                        value={formData.title}
                        onChange={(e) => setField('title', e.target.value)}
                    />
                    {errors.title && <p className="text-xs text-red-600 mt-1">{errors.title}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <ExactLocation formData={formData} setFormData={setFormData} />
                    <div>
                        <label className="form-label">E - Landmark</label>
                        <input
                            className="form-input"
                            value={formData.landmark}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                }
                            }}
                            onChange={(event) => {
                                setFormData({ ...formData, landmark: event.target.value });
                            }}
                        />
                    </div>
                </div>

                <TagInput
                    label="T - Type Of Hazard"
                    value={formData.incidentTypeIds
                        .map((id) => incidentTypes.find((t) => t.id === id)?.incident_type_name)
                        .filter(Boolean)}
                    onChange={(names) => {
                        const ids = names
                            .map((name) => incidentTypes.find((t) => t.incident_type_name === name)?.id)
                            .filter((id) => id != null);
                        setField('incidentTypeIds', ids);
                    }}
                    options={incidentTypes.map((t) => t.incident_type_name)}
                    restrictToOptions
                    placeholder="Search hazard type..."
                />
                {errors.incidentTypeIds && <p className="text-xs text-red-600 -mt-4">{errors.incidentTypeIds}</p>}

                <TagInput
                    label="H - Potential Hazards"
                    value={formData.hazards}
                    onChange={(newValue) => setFormData({ ...formData, hazards: newValue })}
                    options={cbrneHazards}
                    placeholder="Add hazard..."
                />

                <TagInput
                    label="A - Access"
                    value={formData.access}
                    onChange={(newValue) => setFormData({ ...formData, access: newValue })}
                    options={[]}
                    placeholder="Add access point..."
                />

                <div>
                    <label className="form-label">N - Number Of Casualties</label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
                        <div>
                            <label className="text-xs text-neutral-500 mb-1 block dark:text-neutral-500">Total</label>
                            <input
                                type="number"
                                min="0"
                                className={`form-input ${errors.totalCasualities ? 'border-red-400 focus:ring-red-400 focus:border-red-400' : ''}`}
                                value={formData.totalCasualities}
                                onChange={(e) => setField('totalCasualities', e.target.value)}
                            />
                            {errors.totalCasualities && <p className="text-xs text-red-600 mt-1">{errors.totalCasualities}</p>}
                        </div>
                        <div>
                            <label className="text-xs text-neutral-500 mb-1 block dark:text-neutral-500">Green (optional)</label>
                            <input
                                type="number"
                                min="0"
                                className="form-input"
                                value={formData.greenCasualities}
                                onChange={(e) => setField('greenCasualities', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-xs text-neutral-500 mb-1 block dark:text-neutral-500">Yellow (optional)</label>
                            <input
                                type="number"
                                min="0"
                                className="form-input"
                                value={formData.yellowCasualities}
                                onChange={(e) => setField('yellowCasualities', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-xs text-neutral-500 mb-1 block dark:text-neutral-500">Red (optional)</label>
                            <input
                                type="number"
                                min="0"
                                className="form-input"
                                value={formData.redCasualities}
                                onChange={(e) => setField('redCasualities', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-xs text-neutral-500 mb-1 block dark:text-neutral-500">Black (optional)</label>
                            <input
                                type="number"
                                min="0"
                                className="form-input"
                                value={formData.blackCasualities}
                                onChange={(e) => setField('blackCasualities', e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <TagInput
                        label="E - Emergency Services Present"
                        value={formData.erServicePresent}
                        onChange={(newValue) => setFormData({ ...formData, erServicePresent: newValue })}
                        options={EMERGENCY_SERVICES}
                        placeholder="Select or type other..."
                    />
                    <TagInput
                        label="E - Emergency Services Required"
                        value={formData.erServiceRequired}
                        onChange={(newValue) => setFormData({ ...formData, erServiceRequired: newValue })}
                        options={EMERGENCY_SERVICES}
                        placeholder="Select or type other..."
                    />
                </div>

                <div>
                    <label className="form-label">Description</label>
                    <textarea
                        className={`form-input ${errors.description ? 'border-red-400 focus:ring-red-400 focus:border-red-400' : ''}`}
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setField('description', e.target.value)}
                    />
                    {errors.description && <p className="text-xs text-red-600 mt-1">{errors.description}</p>}
                </div>

                <div className="pt-2">
                    <Button type="submit" variant="primary" disabled={submitting}>
                        {submitting ? 'Submitting…' : 'Submit'}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default MethaneForm;
